import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Loader2, Mail, Send, HeartHandshake, PartyPopper } from "lucide-react";
import { sendContact, formatApiErrorDetail } from "@/lib/api";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await sendContact(form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFFDF6]" data-testid="contact-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 pt-6 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#FFC107] shadow-inner">
            <Star className="h-5 w-5 fill-white text-white" />
          </span>
          <span className="font-display text-base font-semibold tracking-tight sm:text-lg">
            Little Songs. <span className="text-[#0284C7]">Big Faith.</span>
          </span>
        </Link>
        <Link
          to="/"
          data-testid="contact-home-link"
          className="rounded-full border border-[#E2DDD0] bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:text-slate-900"
        >
          Back to home
        </Link>
      </header>

      <section className="relative mx-auto grid max-w-5xl gap-12 px-4 pb-24 pt-14 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-10 -left-24 h-80 w-80 rounded-full bg-[#FEF3C7] blur-3xl" />
          <div className="absolute bottom-0 -right-24 h-80 w-80 rounded-full bg-[#FCE7F3] blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-[#FCD34D] bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 shadow-sm">
            <HeartHandshake className="h-3.5 w-3.5 text-[#FF6B6B]" />
            We&apos;d love to hear from you
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl">
            Say hello{" "}
            <span className="bg-gradient-to-r from-[#FFC107] via-[#FF6B6B] to-[#9333EA] bg-clip-text text-transparent">
              anytime
            </span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
            A question about a song, an idea for the pack, a church partnership, or just a hello —
            every message lands straight in our inbox and is read by a real human.
          </p>
          <div className="mt-8 rounded-3xl border border-[#E2DDD0] bg-white/80 p-5">
            <a
              href="mailto:admin@littlesongsbigfaith.com"
              data-testid="contact-direct-email"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0284C7] hover:underline"
            >
              <Mail className="h-4 w-4 text-[#FFC107]" />
              admin@littlesongsbigfaith.com
            </a>
            <p className="mt-2 text-xs text-slate-400">We usually reply within a day or two.</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_24px_48px_-12px_rgba(14,165,233,0.18)]">
            {sent ? (
              <div className="py-8 text-center" data-testid="contact-success">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#DCFCE7]">
                  <PartyPopper className="h-8 w-8 text-[#16A34A]" />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold text-slate-900">Message sent!</h2>
                <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
                  Thank you — your message is in our inbox. We usually reply within a day or two.
                </p>
                <Link
                  to="/sing"
                  data-testid="contact-success-sing-link"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFC107] px-6 py-3 font-display text-sm font-semibold text-slate-900 transition-transform hover:scale-[1.04]"
                >
                  Sing a song while you wait
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl font-bold text-slate-900">Send us a message</h2>
                <form onSubmit={submit} className="mt-5 grid gap-3" data-testid="contact-form">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="contact-name-input"
                    placeholder="Your name"
                    maxLength={80}
                    className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="contact-email-input"
                    placeholder="Your email address"
                    className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    data-testid="contact-message-input"
                    placeholder="What's on your heart?"
                    maxLength={3000}
                    className="resize-none rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                  {/* honeypot — hidden from humans */}
                  <input
                    type="text"
                    value={form.website}
                    onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="contact-honeypot"
                  />
                  {error && (
                    <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="contact-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC107] py-4 font-display text-base font-semibold text-slate-900 shadow-[0_10px_30px_-5px_rgba(234,179,8,0.45)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send message
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Contact;
